import {i18n} from "@/i18n";
import {handleErrorWithToast, type TError} from "@/utils/errorHandlers";
import JSZip from "jszip";
import {useEffect, useState} from "react";
import useDownloader from "react-use-downloader";

type TZipFile = {name: string; url: string};

// Share of the zip download spent fetching the files; the remainder is spent compressing them.
const ZIP_FETCH_SHARE = 0.9;

const handleDownloadErrorWithToast = async (error: unknown) => {
  const downloadError = error as TError;
  const body = downloadError.response?.data;

  if (body instanceof Blob) {
    try {
      const data = JSON.parse(await body.text());
      handleErrorWithToast({error: {...downloadError, response: {...downloadError.response!, data}}});
      return;
    } catch {}
  }

  handleErrorWithToast({error: downloadError, defaultMessage: i18n.t("errors:toast_download_error")});
};

const saveBlob = ({blob, filename}: {blob: Blob; filename: string}) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Streams a single file, reporting the fraction downloaded so far. Falls back to a plain
 * `blob()` read when the response carries no length (progress then only moves per file).
 */
const fetchWithProgress = async ({onProgress, url}: {onProgress: (fraction: number) => void; url: string}) => {
  const response = await fetch(url, {cache: "no-cache"});

  if (!response.ok) throw new Error(`download_failed_${response.status}`);

  const total = Number(response.headers.get("Content-Length") ?? 0);
  const type = response.headers.get("Content-Type") ?? "";

  if (!response.body || !total) return response.blob();

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  while (true) {
    const {done, value} = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.length;
    onProgress(Math.min(loaded / total, 1));
  }

  return new Blob(chunks as BlobPart[], {type});
};

/**
 * Single entry point for the app's download buttons, so they all report progress the same way.
 *
 * - `downloadUrl` — one remote file. Measurable.
 * - `downloadZip` — several remote files bundled into an archive. Measurable across all of them.
 * - `downloadBlob` — a blob produced elsewhere (a server-rendered PDF, a client-side export).
 *   Measurable only when the response carries a length.
 *
 * `progress` is 0-100 while a download is measurable and null otherwise — feed it straight into
 * `Button`'s `progress`. When a download is running but unmeasurable, `progress` stays null and
 * only `isDownloading` is true: pass that to `Button`'s `isLoading` for the usual spinner.
 */
const useDownloadWithProgress = () => {
  const {download, error, isInProgress, percentage} = useDownloader({cache: "no-cache"});
  const [manualProgress, setManualProgress] = useState<number | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    if (error) handleDownloadErrorWithToast(error);
  }, [error]);

  const downloadUrl = async ({filename, url}: {filename: string; url: string}) => {
    await download(url, filename);
  };

  const downloadZip = async ({
    files,
    filename,
    folderName,
  }: {
    files: TZipFile[];
    filename: string;
    folderName?: string;
  }) => {
    setIsBusy(true);
    setManualProgress(0);
    try {
      const zip = new JSZip();
      const folder = zip.folder(folderName ?? "")!;

      for (const [index, file] of files.entries()) {
        const blob = await fetchWithProgress({
          onProgress: fraction => setManualProgress(((index + fraction) / files.length) * ZIP_FETCH_SHARE * 100),
          url: file.url,
        });
        folder.file(file.name, blob);
        setManualProgress(((index + 1) / files.length) * ZIP_FETCH_SHARE * 100);
      }

      const archive = await zip.generateAsync({type: "blob"}, metadata =>
        setManualProgress((ZIP_FETCH_SHARE + (metadata.percent / 100) * (1 - ZIP_FETCH_SHARE)) * 100),
      );
      saveBlob({blob: archive, filename});
    } catch (downloadError) {
      await handleDownloadErrorWithToast(downloadError);
    } finally {
      setManualProgress(null);
      setIsBusy(false);
    }
  };

  const downloadBlob = async ({
    filename,
    getBlob,
  }: {
    filename: string;
    getBlob: (args: {onProgress: (event: {loaded: number; total?: number}) => void}) => Promise<Blob>;
  }) => {
    setIsBusy(true);
    try {
      const blob = await getBlob({
        onProgress: ({loaded, total}) => setManualProgress(total ? Math.min((loaded / total) * 100, 100) : null),
      });
      saveBlob({blob, filename});
    } catch (downloadError) {
      await handleDownloadErrorWithToast(downloadError);
    } finally {
      setManualProgress(null);
      setIsBusy(false);
    }
  };

  return {
    downloadBlob,
    downloadUrl,
    downloadZip,
    isDownloading: isInProgress || isBusy,
    progress: manualProgress ?? (isInProgress ? percentage : null),
  };
};

export default useDownloadWithProgress;
